/**
 * Main Entry Point
 *
 * 2-phase initialization system: Normalize → Default + Animations.
 * Handles module loading, lifecycle management, and SPA coordination.
 *
 * Note: hs-main class is added via inline script in README (for instant application)
 */
import config from '@config';

const API_NAME = config._global.apiName;

const initializeHsMain = async (): Promise<void> => {
  if (window[API_NAME] && !Array.isArray(window[API_NAME]) && window[API_NAME].loaded) {
    return;
  }

  const queuedModules = Array.isArray(window[API_NAME]) ? window[API_NAME] : [];

  const moduleMap = {
    normalize: () => import('./src/modules/normalize/normalize.ts'),
    default: () => import('./src/modules/default/default.ts'),
    animations: () => import('./src/animations/animations.ts'),
  };

  let scripts = [...document.querySelectorAll(`script[type="module"][src="${import.meta.url}"]`)];

  if (scripts.length === 0) {
    scripts = [
      ...document.querySelectorAll('script[type="module"][src*="@hortonstudio/main"]'),
    ].filter((script) => {
      const scriptSrc = (script as HTMLScriptElement).src;
      const currentSrc = import.meta.url;
      const scriptPackage = scriptSrc.match(/@hortonstudio\/main(@[\d.]+)?/)?.[0];
      const currentPackage = currentSrc.match(/@hortonstudio\/main(@[\d.]+)?/)?.[0];
      return (
        scriptPackage &&
        currentPackage &&
        scriptPackage.split('@')[0] === currentPackage.split('@')[0]
      );
    });
  }

  // Read settings from script tag attributes
  const settings = {
    isSPA: scripts.some((script) => script.getAttribute('data-hs-spa') === 'true'),
  };

  // Define 2-phase initialization system
  const PHASES = {
    normalize: {
      modules: ['normalize'],
      parallel: false, // Sequential execution critical for DOM fixes
    },
    default: {
      modules: ['default', 'animations'],
      parallel: true, // Run default and animations in parallel
    },
  };

  const loadModule = async (moduleName: string): Promise<unknown> => {
    const instance = window[API_NAME];
    if (instance.process.has(moduleName)) {
      return instance.modules[moduleName]?.loading;
    }

    instance.process.add(moduleName);

    const moduleData = instance.modules[moduleName] || {};
    instance.modules[moduleName] = moduleData;

    moduleData.loading = new Promise((resolve, reject) => {
      moduleData.resolve = resolve;
      moduleData.reject = reject;
    });

    let destroy: (() => void) | undefined;
    try {
      const { init } = await moduleMap[moduleName]();
      const result = await init();
      const resultData = result || {};
      destroy = resultData.destroy as (() => void) | undefined;
      const initResult = resultData.result;

      moduleData.destroy = () => {
        destroy?.();
        instance.process.delete(moduleName);
      };
      moduleData.restart = () => {
        moduleData.destroy?.();
        instance.load(moduleName);
      };

      moduleData.resolve?.(initResult);
      delete moduleData.resolve;
      delete moduleData.reject;

      return initResult;
    } catch (error) {
      destroy?.();
      moduleData.reject?.(error);
      instance.process.delete(moduleName);
      throw error;
    }
  };

  // Helper to execute a phase (modules already downloaded)
  const executePhase = async (phaseName: string): Promise<void> => {
    const phase = PHASES[phaseName];

    // Execute based on parallel setting
    if (phase.parallel) {
      const initPromises = phase.modules.map((name: string) => loadModule(name));
      await Promise.allSettled(initPromises);
    } else {
      // Sequential execution
      for (const name of phase.modules) {
        await loadModule(name);
      }
    }
  };

  // Trigger DOM ready event
  const triggerDomReady = () => {
    const event = new CustomEvent('hsmain:dom-ready');
    window.dispatchEvent(event);
  };

  // Initialize all modules in 2 phases
  const initializeInPhases = async (): Promise<void> => {
    // Step 1: Download ALL modules in parallel (before any init)
    await Promise.allSettled([
      moduleMap['normalize'](),
      moduleMap['default'](),
      moduleMap['animations'](),
    ]);

    // Step 2: Init in phases
    // Phase 1: Normalize (critical DOM fixes)
    await executePhase('normalize');

    // Phase 2: Default + Animations (parallel)
    await executePhase('default');

    // Trigger DOM ready immediately after phase 2
    triggerDomReady();

    // Phase 3: Initialize scroll animations (independent, non-blocking)
    // Waits for IX3 indefinitely, triggers scroll when ready
    const { initScrollAnimations } = await import('./src/utils/gsap.ts');
    initScrollAnimations().catch((error) => {
      console.error('[hsmain] Scroll animations failed to initialize:', error);
    });
  };

  const readyCallbacks: (() => void)[] = [];

  window[API_NAME] = {
    scripts,
    settings,
    modules: {},
    process: new Set(),
    load: loadModule,
    loaded: false,
    push(...items: [string, ((result: unknown) => void)?][]) {
      for (const [moduleName, callback] of items) {
        if (typeof callback === 'function') {
          this.modules[moduleName]?.loading?.then(callback);
        } else {
          this.load(moduleName);
        }
      }
    },
    destroy() {
      for (const moduleName in this.modules) {
        this.modules[moduleName]?.destroy?.();
      }
    },
    async reinitialize() {
      // Complete teardown
      this.loaded = false;
      this.destroy();
      this.modules = {};
      this.process = new Set();

      // Re-scan for modules including in w-embeds
      await this.scanForEmbedModules();

      // Use same phased initialization flow
      await initializeInPhases();

      this.loaded = true;
    },
    async scanForEmbedModules() {
      const embeds = document.querySelectorAll('.w-embed');
      embeds.forEach((embed) => {
        const scripts = embed.querySelectorAll('script[data-hs-module]');
        scripts.forEach((script) => {
          // Extract module info from data attributes
          const moduleType = script.getAttribute('data-hs-module-type');
          const moduleName = script.getAttribute('data-hs-module');

          if (moduleType && moduleName) {
            const moduleKey = `data-hs-${moduleType}-${moduleName}`;
            if (moduleMap[moduleKey]) {
              this.load(moduleKey);
            }
          }
        });
      });
    },
    afterReady(callback: () => void) {
      if (typeof callback === 'function') {
        if (this.loaded) {
          callback();
        } else {
          readyCallbacks.push(callback);
        }
      }
    },
    status(moduleName?: string) {
      if (moduleName) {
        return {
          loaded: !!this.modules[moduleName],
          loading: this.process.has(moduleName),
        };
      }
      return {
        loaded: Object.keys(this.modules),
        loading: [...this.process],
        phases: PHASES,
      };
    },
  };

  const finalize = async (): Promise<void> => {
    window[API_NAME].push(...queuedModules);

    // Run phased initialization
    await initializeInPhases();

    window[API_NAME].loaded = true;

    // Add ready class to HTML element
    document.documentElement.classList.add(
      config._global.classes.initialized.replace('main', 'ready')
    );

    readyCallbacks.forEach((callback) => {
      try {
        callback();
      } catch {
        // Silent error handling
      }
    });
    readyCallbacks.length = 0; // Clear array after execution
  };

  finalize().catch(() => {});
};

initializeHsMain();
